import React, { useEffect, useState } from "react";
import { useConfirm } from "../common/ConfirmDialog";
import styles from "../../styles";
import { FaLocationArrow } from "react-icons/fa";
export interface AirportOption {
  code: string;
  city: string;
  name: string;
  country?: string;
}

interface AirportSelectProps {
  suggestions: AirportOption[];
  query: string;
  setQuery: (q: string) => void;
  onSelect: (option: AirportOption) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  dropdownRef?: React.RefObject<HTMLDivElement | null>;
}

const AirportSelect: React.FC<AirportSelectProps> = ({
  suggestions,
  query,
  setQuery,
  onSelect,
  isOpen,
  setIsOpen,
  dropdownRef,
}) => {
    const confirm = useConfirm();
  const [locating, setLocating] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef?.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, dropdownRef, setIsOpen]);

  // Handler for location icon click
  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    // Require secure context (HTTPS)
    if (!window.isSecureContext && window.location.protocol !== "https:") {
      alert(
        "Location requires a secure connection (HTTPS). Please use the site over HTTPS or enter airport manually."
      );
      return;
    }

    // Check permission state before prompting where available
    try {
      if (navigator.permissions && (navigator.permissions as any).query) {
        const perm = await (navigator.permissions as any).query({
          name: "geolocation",
        });
        if (perm.state === "denied") {
          alert(
            "Location permission has been denied. Please enable it in your browser settings or enter airport manually."
          );
          return;
        }
      }
    } catch {
      // ignore permission API failures and proceed to prompt
    }

    // Explain why and get explicit consent (coarse location only)
    const consent = await confirm({
      title: "Use your location?",
      message:
        "We can find your nearest airport from your approximate location. " +
        "Exact coordinates are never stored.",
      confirmLabel: "Use my location",
    });
    if (!consent) return;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Reduce precision to avoid exposing exact coordinates (approx ~1km)
          const approxLat = Math.round(position.coords.latitude * 100) / 100;
          const approxLng = Math.round(position.coords.longitude * 100) / 100;

          const res = await fetch("/geo-reverse", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            // send approximate coords and indicate approximation to the server
            body: JSON.stringify({
              lat: approxLat,
              lng: approxLng,
              approximate: true,
            }),
          });
          const data = await res.json();
          if (res.ok && data && data.airport) {
            onSelect(data.airport);
            setIsOpen(false);
          } else {
            alert(data?.error || "No nearby airport found.");
          }
        } catch (err) {
          alert("Failed to get airport from location.");
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        const msg =
          error?.code === 1
            ? "Location access denied."
            : error?.code === 3
              ? "Location request timed out."
              : "Unable to retrieve your location.";
        alert(msg);
        setLocating(false);
      },
      // Request coarse location and limit waiting time / use cached position if available
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef as React.RefObject<HTMLDivElement>}
      className={`
        absolute z-10 mt-2 bg-[#F5F8FF] rounded-lg p-4 shadow-md 
        w-[90vw] sm:w-[60vw] md:w-[40vw] lg:w-[30vw] 
        min-w-[280px] max-w-[420px]
      `}
    >
      {/* Search Input */}
      <div className={`${styles.flexBetween} border-b pb-2`}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 font-sora font-medium text-black outline-none bg-transparent 
            placeholder:text-gray-500 text-sm sm:text-base"
          placeholder="Type airport/city name"
          autoFocus
        />
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={locating}
          className={`${styles.btnMin} focus:outline-none`}
          title="Use current location"
        >
          <FaLocationArrow
            className={`w-4 h-4 text-primary sm:w-[28px] sm:h-[28px] shrink-0 
              ${locating ? "opacity-50 animate-pulse" : ""}`}
          />
        </button>
      </div>

      {/* Suggestions */}
      <div className="max-h-[40vh] overflow-y-auto">
        {suggestions.length === 0 && query.length >= 3 && (
          <p className="text-center text-gray-500 text-sm pt-2">
            No matching airports
          </p>
        )}
        {suggestions.map(({ city, name, code, country }) => (
          <div
            key={code}
            onClick={() => {
              onSelect({ city, name, code, country });
              setIsOpen(false);
            }}
            className={`
              ${styles.flexBetween} flex-wrap gap-1
              hover:bg-white px-3 py-2 rounded-md cursor-pointer
              transition-colors duration-200
              min-h-[44px] sm:min-h-[48px]
            `}
          >
            <span className="text-black text-sm sm:text-base">{city}</span>
            <span className="text-xs sm:text-sm text-heading text-right flex-1 px-2">
              {name}
            </span>
            <span className="text-2xs sm:text-xs text-gray-500">{code}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AirportSelect;
